import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Crosshair, Music, TreePine, Tv, Clock, Beer } from "lucide-react";
import { siteData } from "../data/mock";

const featureIcons = [Crosshair, Music, TreePine, Tv, Clock, Beer];

export const AboutSection = () => {
  const ref = useRef(null);
  const featRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const featInView = useInView(featRef, { once: true, margin: "-80px" });

  return (
    <section id="about" className="relative py-24 md:py-32 bg-[#0d0b08] overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-gold/60 text-xs uppercase tracking-[0.4em] font-light">Our Story</span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-cream mt-4">
            {siteData.about.title}
          </h2>
        </motion.div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Text Side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-2 lg:order-1"
          >
            {siteData.about.paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-cream/60 text-base md:text-lg leading-relaxed mb-6 font-light"
              >
                {p}
              </p>
            ))}

            {/* Pull Quote */}
            <div className="mt-10 pl-6 border-l-2 border-gold/40">
              <p className="text-gold/80 font-heading text-lg md:text-xl italic leading-relaxed">
                "{siteData.about.highlight}"
              </p>
            </div>
          </motion.div>

          {/* Right: Image Side */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative">
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src="/images/interior-3.jpg"
                  alt="Split Decisions bar interior"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0b08]/60 via-transparent to-transparent" />
              </div>

              {/* Overlapping Second Image */}
              <div className="absolute -bottom-8 -left-4 md:-left-8 w-40 md:w-52 h-40 md:h-52 overflow-hidden rounded-sm border-2 border-gold/20 shadow-2xl">
                <img
                  src="/images/interior-1.jpg"
                  alt="Split Decisions pool area"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Gold accent corner */}
              <div className="absolute -top-3 -right-3 w-16 h-16 border-t-2 border-r-2 border-gold/30" />
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div ref={featRef} className="mt-28 md:mt-36">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={featInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="text-center mb-14"
          >
            <span className="text-gold/60 text-xs uppercase tracking-[0.4em] font-light">What We Bring</span>
            <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl text-cream mt-4">
              More Than Just a Bar
            </h3>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {siteData.features.map((feat, i) => {
              const Icon = featureIcons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 25 }}
                  animate={featInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group bg-[#161412] border border-gold/8 rounded-sm p-7 hover:border-gold/25 transition-colors duration-500"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(201,169,110,0.1)' }}>
                      <Icon size={18} style={{ color: '#c9a96e' }} />
                    </div>
                    <h4 className="font-heading text-lg text-cream">{feat.title}</h4>
                  </div>
                  <p className="text-cream/50 text-sm font-light leading-relaxed">
                    {feat.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
    </section>
  );
};
